import supabase, { getSupabaseBaseUrl } from "@/services/supabaseClient";
import { hydrateDependantAvatar } from "@/services/dependantCareService.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";
import { resolvePatientDisplayName } from "@/utils/patientDisplayName.js";

export const PATIENT_AVATAR_PLACEHOLDER = "/icons/circular-avatar-placeholder.svg";

function safeText(value) {
  return String(value || "").trim();
}

function fullNameFromParts(firstName, lastName) {
  return [safeText(firstName), safeText(lastName)].filter(Boolean).join(" ").trim();
}

function firstNonEmpty(values, fallback = "") {
  for (const value of values) {
    if (value === 0 || value === false) return value;
    if (value != null && String(value).trim() !== "") return value;
  }
  return fallback;
}

function safeDate(value) {
  const nextDate = value ? new Date(value) : null;
  if (!nextDate || Number.isNaN(nextDate.getTime())) return null;
  return nextDate;
}

function calcAge(dateOfBirth) {
  const dob = safeDate(dateOfBirth);
  if (!dob) return null;

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 && age <= 120 ? age : null;
}

function normalizeGender(raw) {
  const value = safeText(raw).toLowerCase();
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function resolvePatientAvatarUrl(url, path, fallback = PATIENT_AVATAR_PLACEHOLDER) {
  const direct = safeText(url);
  if (direct) return direct;

  const cleanPath = safeText(path);
  if (cleanPath) {
    if (/^https?:\/\//i.test(cleanPath)) return cleanPath;

    const base = getSupabaseBaseUrl();
    if (base) {
      return `${base}/storage/v1/object/public/patient-profile-images/${cleanPath.replace(/^public\//, "")}`;
    }
  }

  return fallback;
}

// Notification presentation may enrich an already-authorised Patient avatar
// progressively. A signing failure leaves the governed row usable.
export async function hydratePatientProfileAvatar(row = {}, client = supabase) {
  const path = safeText(
    row?.profile_photo_path ||
      row?.profile_photo_storage_path ||
      row?.avatar_path,
  ).replace(/^public\//, "");
  const direct = safeText(row?.profile_image_url);

  if (!path) {
    return {
      ...row,
      _avatarHydration: { path, signed: Boolean(direct), reason: direct ? "direct_without_path" : "path_missing" },
    };
  }

  let signingError = "";
  try {
    const { data, error } = await client.storage
      .from("patient-profile-images")
      .createSignedUrl(path, 60 * 60 * 24);
    if (!error && data?.signedUrl) {
      return {
        ...row,
        profile_photo_path: path,
        profile_photo_storage_path: path,
        profile_image_url: data.signedUrl,
        _avatarHydration: { path, signed: true, reason: "signed" },
      };
    }
    signingError = safeText(error?.message || error);
  } catch (error) {
    signingError = safeText(error?.message || error);
  }

  return {
    ...row,
    profile_photo_path: path,
    profile_photo_storage_path: path,
    profile_image_url: direct || "",
    _avatarHydration: { path, signed: false, reason: "sign_failed", error: signingError },
  };
}

// A clinician receives only the identity of patients on appointments assigned
// to that clinician. The hosted RPC verifies the caller; no profile table or
// Storage list access is opened to unrelated clinicians.
export async function loadClinicianAppointmentPatientIdentities(
  appointmentRows,
  client = supabase,
) {
  const appointmentIds = [...new Set((appointmentRows || []).map((row) => safeText(row?.id)).filter(Boolean))];
  if (!appointmentIds.length) return new Map();
  const { data, error } = await client.rpc("afrimed_clinician_appointment_patient_identity_v2", {
    p_appointment_ids: appointmentIds,
  });
  if (error) throw error;

  const rows = await Promise.all((Array.isArray(data) ? data : []).map(async (row) => {
    const avatar = await hydratePatientProfileAvatar({
      profile_photo_path: row.avatar_path,
      profile_image_url: row.avatar_url,
    }, client);
    return { row, avatar };
  }));
  return new Map(rows.map(({ row, avatar }) => {
    const image = safeText(avatar.profile_image_url) || PATIENT_AVATAR_PLACEHOLDER;
    const legalName = safeText(row.patient_name) || "Patient";
    const isSelf = safeText(row.care_subject_type || "self").toLowerCase() === "self";
    const patientName = isSelf ? resolvePatientDisplayName({
      profile: {
        salutation: row.patient_salutation,
        last_name: row.patient_last_name,
        full_name: legalName,
      },
      fallback: legalName,
    }) : legalName;
    return [safeText(row.appointment_id), {
      patientName,
      patientLegalName: legalName,
      patientAvatar: image,
      avatar: image,
      patientRowId: safeText(row.patient_row_id),
      patientUserId: safeText(row.patient_user_id),
      careSubjectType: safeText(row.care_subject_type) || "self",
      patientProfile: {
        user_id: safeText(row.patient_user_id),
        patient_id: safeText(row.patient_row_id),
        full_name: legalName,
        salutation: isSelf ? safeText(row.patient_salutation) : "",
        last_name: isSelf ? safeText(row.patient_last_name) : "",
        date_of_birth: isSelf ? safeText(row.patient_date_of_birth) : "",
        profile_photo_path: safeText(row.avatar_path),
        profile_image_url: image,
      },
      authProfile: null,
      _avatarHydration: avatar._avatarHydration,
    }];
  }));
}

function extractPatientPhotoPaths(row) {
  if (!Array.isArray(row?.patient_photo_paths)) return [];
  return row.patient_photo_paths
    .map((value) => safeText(value))
    .filter(Boolean);
}

function mergeProfileRow(existingRow, incomingRow) {
  const next = {
    ...(existingRow || {}),
  };

  Object.entries(incomingRow || {}).forEach(([key, value]) => {
    if (value === 0 || value === false) {
      next[key] = value;
      return;
    }

    if (value == null) return;
    if (typeof value === "string" && value.trim() === "") return;

    next[key] = value;
  });

  return {
    ...next,
    patient_photo_paths:
      extractPatientPhotoPaths(incomingRow).length > 0
        ? extractPatientPhotoPaths(incomingRow)
        : extractPatientPhotoPaths(existingRow),
  };
}

function mergePatientProfile(profileMaps, userId, row, patientRowId = "") {
  const nextUserId = safeText(userId);
  const nextPatientRowId = safeText(patientRowId);
  const profileRow = row && typeof row === "object" ? row : null;
  if (!nextUserId || !profileRow) return;

  const currentByUser = profileMaps.patientProfilesByUserId.get(nextUserId) || null;
  profileMaps.patientProfilesByUserId.set(nextUserId, mergeProfileRow(currentByUser, profileRow));

  if (nextPatientRowId) {
    const currentByPatient = profileMaps.patientProfilesByPatientId.get(nextPatientRowId) || null;
    profileMaps.patientProfilesByPatientId.set(
      nextPatientRowId,
      mergeProfileRow(currentByPatient, profileRow),
    );
  }
}

function buildPatientIdentityContext({
  appointmentRow = null,
  ref = "",
  patientRow = null,
  patientProfile = null,
  authProfile = null,
}) {
  const nestedPatient = Array.isArray(appointmentRow?.patient)
    ? appointmentRow.patient[0] || null
    : appointmentRow?.patient || null;

  const userId =
    safeText(nestedPatient?.user_id) ||
    safeText(patientRow?.user_id) ||
    safeText(patientProfile?.user_id) ||
    safeText(authProfile?.user_id) ||
    safeText(authProfile?.id) ||
    safeText(ref);

  const name =
    (safeText(appointmentRow?.care_subject_type).toLowerCase() === "dependant"
      ? safeText(appointmentRow?.care_subject_name)
      : "") ||
    safeText(nestedPatient?.full_name) ||
    safeText(nestedPatient?.display_name) ||
    safeText(patientProfile?.full_name) ||
    fullNameFromParts(patientProfile?.first_name, patientProfile?.last_name) ||
    safeText(authProfile?.display_name) ||
    safeText(authProfile?.full_name) ||
    fullNameFromParts(authProfile?.first_name, authProfile?.last_name) ||
    "Patient";

  const avatar = firstNonEmpty(
    [
      resolvePatientAvatarUrl(
        patientProfile?.profile_image_url || patientProfile?.avatar_url,
        patientProfile?.profile_photo_path || patientProfile?.avatar_path,
        "",
      ),
      safeText(nestedPatient?.avatar_url),
      PATIENT_AVATAR_PLACEHOLDER,
    ],
    PATIENT_AVATAR_PLACEHOLDER,
  );

  const dob = firstNonEmpty(
    [
      safeText(appointmentRow?.care_subject_type).toLowerCase() === "dependant"
        ? appointmentRow?.care_subject_date_of_birth
        : null,
      patientProfile?.date_of_birth,
      patientProfile?.dob,
      nestedPatient?.date_of_birth,
      nestedPatient?.dob,
      null,
    ],
    null,
  );
  const gender = normalizeGender(
    firstNonEmpty(
      [patientProfile?.gender, patientProfile?.sex, nestedPatient?.gender, nestedPatient?.sex, ""],
      "",
    ),
  );
  const guardianName =
    safeText(authProfile?.display_name) ||
    safeText(authProfile?.full_name) ||
    fullNameFromParts(authProfile?.first_name, authProfile?.last_name) ||
    safeText(patientProfile?.full_name) ||
    fullNameFromParts(patientProfile?.first_name, patientProfile?.last_name) ||
    name;

  return {
    patientName: name,
    patientAvatar: avatar,
    patientUserId: userId,
    patientRowId: safeText(patientRow?.id),
    patientProfile,
    authProfile,
    verificationStatus: normalizeVerificationStatus(
      firstNonEmpty(
        [
          patientProfile?.id_verification_status,
          patientProfile?.verification_status,
          authProfile?.id_verification_status,
          authProfile?.verification_status,
          nestedPatient?.id_verification_status,
          nestedPatient?.verification_status,
          "",
        ],
        "",
      ),
      "",
    ),
    dob,
    age: calcAge(dob),
    gender,
    avatar,
    patientPhotoPaths: extractPatientPhotoPaths(patientProfile),
    guardianUserId: userId,
    guardianName,
    careSubjectType:
      safeText(appointmentRow?.care_subject_type).toLowerCase() === "dependant"
        ? "dependant"
        : "self",
    dependantId: safeText(appointmentRow?.care_subject_dependant_id),
    responsibleRelationship: safeText(appointmentRow?.responsible_relationship),
  };
}

function buildDependantIdentityContext({
  appointmentRow = null,
  patientRow = null,
  patientProfile = null,
  authProfile = null,
  dependantRow = null,
}) {
  const guardianUserId =
    safeText(appointmentRow?.guardian_user_id) ||
    safeText(patientRow?.user_id) ||
    safeText(authProfile?.user_id) ||
    safeText(authProfile?.id);

  const guardianName =
    safeText(authProfile?.display_name) ||
    safeText(authProfile?.full_name) ||
    fullNameFromParts(authProfile?.first_name, authProfile?.last_name) ||
    safeText(patientProfile?.full_name) ||
    fullNameFromParts(patientProfile?.first_name, patientProfile?.last_name) ||
    "Guardian";

  const dependantName =
    safeText(appointmentRow?.care_subject_name) ||
    fullNameFromParts(dependantRow?.first_name, dependantRow?.last_name) ||
    "Dependant";

  const dependantDob =
    firstNonEmpty(
      [
        appointmentRow?.care_subject_date_of_birth,
        dependantRow?.date_of_birth,
        null,
      ],
      null,
    );

  return {
    patientName: dependantName,
    patientAvatar:
      safeText(dependantRow?.avatarSrc) ||
      resolvePatientAvatarUrl(
        dependantRow?.avatar_url,
        dependantRow?.avatar_path,
        PATIENT_AVATAR_PLACEHOLDER,
      ),
    patientUserId: guardianUserId,
    patientRowId: safeText(patientRow?.id),
    patientProfile,
    authProfile,
    verificationStatus: normalizeVerificationStatus(
      firstNonEmpty(
        [
          dependantRow?.verification_status,
          patientProfile?.verification_status,
          authProfile?.verification_status,
          "",
        ],
        "",
      ),
      "",
    ),
    dob: dependantDob,
    age: calcAge(dependantDob),
    gender: "",
    avatar:
      safeText(dependantRow?.avatarSrc) ||
      resolvePatientAvatarUrl(
        dependantRow?.avatar_url,
        dependantRow?.avatar_path,
        PATIENT_AVATAR_PLACEHOLDER,
      ),
    patientPhotoPaths: [
      safeText(dependantRow?.avatar_path),
    ].filter(Boolean),
    guardianUserId,
    guardianName,
    careSubjectType: "dependant",
    dependantId:
      safeText(appointmentRow?.care_subject_dependant_id) || safeText(dependantRow?.id),
    relationship:
      safeText(appointmentRow?.care_subject_relationship) ||
      safeText(dependantRow?.relationship),
    responsibleRelationship:
      safeText(appointmentRow?.responsible_relationship) ||
      safeText(appointmentRow?.care_subject_relationship) ||
      safeText(dependantRow?.relationship),
  };
}

export async function loadPatientIdentityContextsByAppointmentId(
  appointmentRows,
  client = supabase,
) {
  const rows = Array.isArray(appointmentRows) ? appointmentRows : [];
  const appointments = rows.filter((row) => safeText(row?.id));
  if (appointments.length === 0) return new Map();

  const refs = Array.from(
    new Set(appointments.map((row) => safeText(row?.patient_id)).filter(Boolean)),
  );
  if (refs.length === 0) return new Map();

  const buildFallbackIdentities = () => {
    const fallback = new Map();
    appointments.forEach((row) => {
      fallback.set(
        safeText(row?.id),
        buildPatientIdentityContext({
          appointmentRow: row,
          ref: safeText(row?.patient_id),
          patientRow: null,
          patientProfile: null,
          authProfile: null,
        }),
      );
    });
    return fallback;
  };

  try {
    const [{ data: patientsByIdRows }, { data: patientsByUserRows }] = await Promise.all([
      client.from("patients").select("id, user_id").in("id", refs),
      client.from("patients").select("id, user_id").in("user_id", refs),
    ]);

    const patientsById = new Map();
    const patientsByUserId = new Map();

    [
      ...(Array.isArray(patientsByIdRows) ? patientsByIdRows : []),
      ...(Array.isArray(patientsByUserRows) ? patientsByUserRows : []),
    ].forEach((row) => {
      const id = safeText(row?.id);
      const userId = safeText(row?.user_id);
      if (id) patientsById.set(id, row);
      if (userId) patientsByUserId.set(userId, row);
    });

    const patientUserIds = Array.from(
      new Set(
        refs
          .map((ref) => {
            const patientRow = patientsById.get(ref) || patientsByUserId.get(ref) || null;
            return safeText(patientRow?.user_id) || safeText(ref);
          })
          .filter(Boolean),
      ),
    );
    const patientRowIds = Array.from(
      new Set(
        refs
          .map((ref) => {
            const patientRow = patientsById.get(ref) || patientsByUserId.get(ref) || null;
            return safeText(patientRow?.id);
          })
          .filter(Boolean),
      ),
    );
    const dependantIds = Array.from(
      new Set(
        appointments
          .map((row) => safeText(row?.care_subject_dependant_id))
          .filter(Boolean),
      ),
    );
    const profileLookupIds = Array.from(new Set([...refs, ...patientUserIds].filter(Boolean)));

    const profileMaps = {
      patientProfilesByUserId: new Map(),
      patientProfilesByPatientId: new Map(),
    };

    const [
      patientProfilesRes,
      authProfilesByUserRes,
      authProfilesByIdRes,
      ehrPatientInfoRes,
      dependantRowsRes,
    ] =
      await Promise.all([
        patientUserIds.length > 0
          ? client
              .from("patient_profiles")
              .select("*")
              .in("user_id", patientUserIds)
          : Promise.resolve({ data: [] }),
        profileLookupIds.length > 0
          ? client
              .from("profiles")
              .select("*")
              .in("user_id", profileLookupIds)
          : Promise.resolve({ data: [] }),
        profileLookupIds.length > 0
          ? client
              .from("profiles")
              .select("*")
              .in("id", profileLookupIds)
          : Promise.resolve({ data: [] }),
        patientUserIds.length > 0
          ? client
              .from("ehr_patient_info")
              .select(
                "patient_id, date_of_birth, primary_language, height_cm, weight_kg, blood_type, current_medications, allergies, chronic_conditions, is_organ_donor, created_at, updated_at",
              )
              .in("patient_id", patientUserIds)
          : Promise.resolve({ data: [] }),
        dependantIds.length > 0
          ? client
              .from("patient_dependants")
              .select("*")
              .in("id", dependantIds)
          : Promise.resolve({ data: [] }),
      ]);

    (Array.isArray(patientProfilesRes?.data) ? patientProfilesRes.data : []).forEach((row) => {
      const rowUserId = safeText(row?.user_id);
      const patientLink = patientsByUserId.get(rowUserId) || null;
      mergePatientProfile(profileMaps, rowUserId, row, patientLink?.id || "");
    });

    (Array.isArray(ehrPatientInfoRes?.data) ? ehrPatientInfoRes.data : []).forEach((row) => {
      const userId = safeText(row?.patient_id);
      const patientLink = patientsByUserId.get(userId) || null;
      const patientRowId = safeText(patientLink?.id);
      if (!userId) return;

      mergePatientProfile(
        profileMaps,
        userId,
        {
          date_of_birth: row?.date_of_birth || null,
          primary_language: row?.primary_language || null,
          height_cm: row?.height_cm ?? null,
          weight_kg: row?.weight_kg ?? null,
          blood_type: row?.blood_type || null,
          current_medications: row?.current_medications || null,
          allergies: row?.allergies || null,
          chronic_conditions: row?.chronic_conditions || null,
          is_organ_donor:
            typeof row?.is_organ_donor === "boolean" ? row.is_organ_donor : null,
          organ_donor: typeof row?.is_organ_donor === "boolean" ? row.is_organ_donor : null,
          created_at: row?.updated_at || row?.created_at || null,
        },
        patientRowId,
      );
    });

    const authProfilesByAnyId = new Map();
    [
      ...(Array.isArray(authProfilesByUserRes?.data) ? authProfilesByUserRes.data : []),
      ...(Array.isArray(authProfilesByIdRes?.data) ? authProfilesByIdRes.data : []),
    ].forEach((row) => {
      const id = safeText(row?.id);
      const userId = safeText(row?.user_id);
      if (id && !authProfilesByAnyId.has(id)) authProfilesByAnyId.set(id, row);
      if (userId && !authProfilesByAnyId.has(userId)) authProfilesByAnyId.set(userId, row);
    });
    const hydratedDependantRows = await Promise.all(
      (Array.isArray(dependantRowsRes?.data) ? dependantRowsRes.data : []).map((row) =>
        hydrateDependantAvatar(row, client),
      ),
    );

    const dependantById = new Map(
      hydratedDependantRows.map((row) => [
        safeText(row?.id),
        row,
      ]),
    );

    const identities = new Map();
    appointments.forEach((row) => {
      const appointmentId = safeText(row?.id);
      const ref = safeText(row?.patient_id);
      const patientRow = patientsById.get(ref) || patientsByUserId.get(ref) || null;
      const patientUserId = safeText(patientRow?.user_id) || ref;
      const patientProfile =
        profileMaps.patientProfilesByUserId.get(patientUserId) ||
        profileMaps.patientProfilesByPatientId.get(safeText(patientRow?.id)) ||
        profileMaps.patientProfilesByPatientId.get(ref) ||
        null;
      const authProfile =
        authProfilesByAnyId.get(patientUserId) || authProfilesByAnyId.get(ref) || null;
      const dependantRow = dependantById.get(safeText(row?.care_subject_dependant_id)) || null;

      const identity =
        safeText(row?.care_subject_type).toLowerCase() === "dependant" && dependantRow
          ? buildDependantIdentityContext({
              appointmentRow: row,
              patientRow,
              patientProfile,
              authProfile,
              dependantRow,
            })
          : buildPatientIdentityContext({
              appointmentRow: row,
              ref,
              patientRow,
              patientProfile,
              authProfile,
            });

      identities.set(
        appointmentId,
        identity,
      );
    });

    return identities;
  } catch (error) {
    console.warn("loadPatientIdentityContextsByAppointmentId fallback:", error?.message || error);
    return buildFallbackIdentities();
  }
}

export async function loadPatientIdentitiesByAppointmentId(
  appointmentRows,
  client = supabase,
) {
  return loadPatientIdentityContextsByAppointmentId(appointmentRows, client);
}
